/**
 * Enterprise-grade logging service for KernLogic
 * Provides structured logging with context, multiple levels, and production-ready output
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

export interface LogContext {
  component?: string;
  userId?: string;
  sessionId?: string;
  importId?: number;
  requestId?: string;
  [key: string]: any;
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: Error;
  stack?: string;
}

class Logger {
  private isDevelopment: boolean;
  private isProduction: boolean;
  private logLevel: LogLevel;

  constructor() {
    this.isDevelopment = import.meta.env.MODE === 'development';
    this.isProduction = import.meta.env.PROD;
    this.logLevel = this.getLogLevel();
  }

  private getLogLevel(): LogLevel {
    const envLevel = import.meta.env.VITE_LOG_LEVEL?.toLowerCase() as LogLevel;
    if (envLevel && ['debug', 'info', 'warn', 'error', 'fatal'].includes(envLevel)) {
      return envLevel;
    }
    // Only show errors and fatal in all environments
    return 'error';
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
      fatal: 4,
    };
    return levels[level] >= levels[this.logLevel];
  }

  private formatMessage(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error
  ): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: context ? { ...context } : undefined,
    };

    if (error) {
      entry.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      } as Error;
      entry.stack = error.stack;
    }

    return entry;
  }

  private output(entry: LogEntry): void {
    if (!this.shouldLog(entry.level)) return;

    if (this.isDevelopment) {
      // Development: Pretty console output
      const contextStr = entry.context
        ? ` [${Object.entries(entry.context)
            .map(([k, v]) => `${k}:${v}`)
            .join(', ')}]`
        : '';
      const prefix = `[${entry.timestamp}] ${entry.level.toUpperCase()}${contextStr}:`;

      switch (entry.level) {
        case 'debug':
          // eslint-disable-next-line no-console
          console.debug(prefix, entry.message, entry.error || '');
          break;
        case 'info':
          // eslint-disable-next-line no-console
          console.info(prefix, entry.message, entry.error || '');
          break;
        case 'warn':
          // eslint-disable-next-line no-console
          console.warn(prefix, entry.message, entry.error || '');
          break;
        case 'error':
        case 'fatal':
          // eslint-disable-next-line no-console
          console.error(prefix, entry.message, entry.error || '');
          // eslint-disable-next-line no-console
          if (entry.stack) console.error('Stack:', entry.stack);
          break;
      }
    } else {
      // Production: Structured JSON logging
      // eslint-disable-next-line no-console
      console.log(JSON.stringify(entry));
    }
  }

  debug(message: string, context?: LogContext): void {
    const entry = this.formatMessage('debug', message, context);
    this.output(entry);
  }

  info(message: string, context?: LogContext): void {
    const entry = this.formatMessage('info', message, context);
    this.output(entry);
  }

  warn(message: string, context?: LogContext, error?: Error): void {
    const entry = this.formatMessage('warn', message, context, error);
    this.output(entry);
  }

  error(message: string, context?: LogContext, error?: Error): void {
    const entry = this.formatMessage('error', message, context, error);
    this.output(entry);
  }

  fatal(message: string, context?: LogContext, error?: Error): void {
    const entry = this.formatMessage('fatal', message, context, error);
    this.output(entry);
  }

  /**
   * Create a child logger with preset context
   */
  child(context: LogContext): Logger {
    const childLogger = new Logger();
    const originalOutput = childLogger.output.bind(childLogger);

    childLogger.output = (entry: LogEntry) => {
      entry.context = { ...context, ...entry.context };
      originalOutput(entry);
    };

    return childLogger;
  }

  /**
   * Performance timing utility
   */
  time(label: string, context?: LogContext): () => void {
    const start = performance.now();
    this.debug(`Timer started: ${label}`, context);

    return () => {
      const duration = performance.now() - start;
      this.info(`Timer completed: ${label} (${duration.toFixed(2)}ms)`, {
        ...context,
        duration,
        label,
      });
    };
  }
}

// Export singleton instance
export const logger = new Logger();

// Export factory for component-specific loggers
export const createLogger = (component: string, additionalContext?: LogContext): Logger => {
  return logger.child({ component, ...additionalContext });
};
